'use client';

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  DEFAULT_LANGUAGE,
  getUserLanguage,
  setUserLanguage as persistUserLanguage,
  translate,
  type SupportedLanguage,
  LANGUAGE_CHANGE_EVENT,
  LANGUAGE_STORAGE_KEY,
} from '@/lib/i18n';

interface LanguageContextValue {
  language: SupportedLanguage;
  setLanguage: (lang: string) => void;
  t: (key: string) => string;
  dir: 'ltr' | 'rtl';
}

const LanguageContext = createContext<LanguageContextValue>({
  language: DEFAULT_LANGUAGE,
  setLanguage: () => {},
  t: (key: string) => translate(key, DEFAULT_LANGUAGE),
  dir: 'ltr',
});

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<SupportedLanguage>(DEFAULT_LANGUAGE);

  useEffect(() => {
    const initial = getUserLanguage();
    setLanguageState(initial);
    document.documentElement.lang = initial;
    document.documentElement.dir = initial === 'ar' ? 'rtl' : 'ltr';

    const handleLanguageChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ language: SupportedLanguage }>;
      if (customEvent.detail?.language) {
        setLanguageState(customEvent.detail.language);
      }
    };

    const handleStorage = (e: StorageEvent) => {
      if (e.key === LANGUAGE_STORAGE_KEY) {
        setLanguageState(getUserLanguage());
      }
    };

    window.addEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const setLanguage = useCallback((lang: string) => {
    persistUserLanguage(lang);
    setLanguageState(getUserLanguage());
  }, []);

  const t = useCallback(
    (key: string) => {
      return translate(key, language);
    },
    [language]
  );

  const value = {
    language,
    setLanguage,
    t,
    dir: language === 'ar' ? ('rtl' as const) : ('ltr' as const),
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
